import * as z from "@-/com.npmjs.zod";

export const BACKUP_INTERVAL_MS = 60 * 60 * 1000;
export const BACKUP_RETENTION_MS = 24 * BACKUP_INTERVAL_MS;

const backupReasonSchema = z.enum([
  "hourly",
  "manual",
  "before-sign-in",
  "before-sign-out",
  "before-import",
  "before-restore",
  "before-cloud-load",
]);

const learningPreferencesSchema = z.strictObject({
  introduceNewCardsEnabled: z.boolean(),
  isAnswerBarHidden: z.boolean(),
  isAutoplayEnabled: z.boolean(),
  isMuted: z.boolean(),
});

/** Web: a backup stored as encrypted 64-row chunks in the browser's backup store. */
export const archiveBackupManifestSchema = z.strictObject({
  chunks: z.array(
    z.strictObject({ key: z.string(), rows: z.number().int().positive(), table: z.string() }),
  ),
  createdAt: z.number().int().nonnegative(),
  // Native ABI ≤4 jobs stored raw SQLite column values; JavaScript-built archives decoded rows.
  encoding: z.literal("sqlite").optional(),
  id: z.string().uuid(),
  learningPreferences: learningPreferencesSchema.optional(),
  reason: backupReasonSchema,
  rowCount: z.number().int().nonnegative(),
  sourceUserId: z.string().min(1),
  version: z.literal(1),
});

/**
 * iOS and Android: a backup is a standalone SQLite copy of the study database on disk
 * (`haniverse-backup-engine.{swift,kt}`). `integrity` records damage found in the copy; a damaged
 * database is still backed up, byte for byte.
 */
export const fileBackupManifestSchema = z.strictObject({
  bytes: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
  files: z.array(z.string()).min(1),
  id: z.string().uuid(),
  integrity: z.union([
    z.strictObject({ ok: z.literal(true) }),
    z.strictObject({ detail: z.string(), ok: z.literal(false) }),
  ]),
  learningPreferences: learningPreferencesSchema.optional(),
  reason: backupReasonSchema,
  rowCount: z.number().int().nonnegative(),
  sourceUserId: z.string().min(1),
  tables: z.record(z.string(), z.number().int().nonnegative()),
  version: z.literal(2),
});

export type ArchiveBackupManifest = z.infer<typeof archiveBackupManifestSchema>;
export type FileBackupManifest = z.infer<typeof fileBackupManifestSchema>;
export type BackupManifest = ArchiveBackupManifest | FileBackupManifest;
export type BackupReason = BackupManifest["reason"];

export interface BackupStore {
  get(key: string): Promise<string | null>;
  keys(prefix: string): Promise<string[]>;
  remove(key: string): Promise<void>;
  set(key: string, value: string, deadline?: number): Promise<void>;
}

export function isBackupExpired(createdAt: number, now: number): boolean {
  return now - createdAt >= BACKUP_RETENTION_MS;
}

export function isBackupDue(lastSuccess: number | undefined, now: number): boolean {
  return lastSuccess === undefined || now < lastSuccess || now - lastSuccess >= BACKUP_INTERVAL_MS;
}
